import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { Filters } from './data.service';
import { catchError, tap } from 'rxjs/operators';
import { AppConstants } from 'src/app/app.constants';
import { ErrorHandlerService } from 'src/app/core/services/error-handler.service';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  private currentStepSubject = new BehaviorSubject<string>('calendar');
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private filtersSubject = new BehaviorSubject<Filters>({});
  private showDrugModalSubject = new BehaviorSubject<boolean>(false);
  private pdufaInformationSubject = new BehaviorSubject<any>(null);
  private editModeSubject = new BehaviorSubject<boolean>(false);
  private editItemSubject = new BehaviorSubject<any>(null);

  currentStep$ = this.currentStepSubject.asObservable();
  loading$ = this.loadingSubject.asObservable();
  filters$ = this.filtersSubject.asObservable();
  showDrugModal$ = this.showDrugModalSubject.asObservable();
  pdufaInformation$ = this.pdufaInformationSubject.asObservable();
  editMode$ = this.editModeSubject.asObservable();
  editItem$ = this.editItemSubject.asObservable();

  private stepOrder = ['calendar', 'type', 'category', 'therapeutic', 'results'];
  constructor(private http: HttpClient, private errorHandlerService: ErrorHandlerService) { }
  init() {
    this.showLoading();

  }

  navigateToStep(step: string) {
    this.currentStepSubject.next(step);
  }

  canNavigateToStep(step: string): boolean {
    const currentIndex = this.stepOrder.indexOf(this.currentStepSubject.value);
    const targetIndex = this.stepOrder.indexOf(step);
    return targetIndex <= currentIndex;
  }

  updateFilters(newFilters: Partial<Filters>) {
    const currentFilters = this.filtersSubject.value;
    const updatedFilters = { ...currentFilters, ...newFilters };
    this.filtersSubject.next(updatedFilters);
  }

  resetFilters() {
    this.filtersSubject.next({});
    // Also reset edit mode when clearing filters
    this.editModeSubject.next(false);
    this.editItemSubject.next(null);
  }

  getFilters(): Filters {
    return this.filtersSubject.value;
  }

  showLoading() {
    this.loadingSubject.next(true);
  }

  hideLoading() {
    this.loadingSubject.next(false);
  }

  openDrugModal() {
    this.showDrugModalSubject.next(true);
  }

  closeDrugModal() {
    this.showDrugModalSubject.next(false);
    this.editModeSubject.next(false);
    this.editItemSubject.next(null);
  }

  openEditModal(item: any) {
    this.editItemSubject.next(item);
    this.editModeSubject.next(true);
    this.showDrugModalSubject.next(true);
  }

  startEditFlow(item: any) {
    console.log('Starting edit flow for item:', item);
    console.log('Item categories:', item.categories);
    console.log('Item application_type:', item.application_type);
    console.log('Item therapeutic_categories:', item.therapeutic_categories);
    console.log('Item sub_categories:', item.sub_categories);

    // Pre-populate filters with existing data
    const filters: any = {
      type: item.application_type,
      category: item.application_type === 'sNDA' ? item.categories : item.categories?.[0],
      therapeuticArea: item.therapeutic_categories,
      subCategory: item.sub_categories?.['Existing Active Moiety (EAM)'] || null,
      subCategories: item.sub_categories?.['Existing Active Moiety (EAM)'] || [],
      therapeuticAreas: item.therapeutic_categories ? item.therapeutic_categories : [],
      categories: (item.application_type === 'sNDA' ? item.categories : item.categories) || []// For sNDA multiple selection
    };


    // Update filters with existing data
    this.filtersSubject.next(filters);

    // Set edit item and mode
    this.editItemSubject.next(item);
    this.editModeSubject.next(true);

    // Navigate directly to category selection step
    this.navigateToStep('category');
  }

  isEditMode(): boolean {
    return this.editModeSubject.value;
  }

  getEditItem(): any {
    return this.editItemSubject.value;
  }

  selectPeriod(type: string, value: string) {
    this.updateFilters({ period: { type, value } });
    setTimeout(() => {
      this.navigateToStep('type');
    }, 300);
  }

  selectType(type: 'NDA' | 'sNDA') {
    this.updateFilters({ type });
    setTimeout(() => {
      this.navigateToStep('category');
    }, 300);
  }

  selectCategory(category: string) {
    // Handle both single category and multiple categories (comma-separated)
    const categories = category.includes(',') ? category.split(',') : [category];
    this.updateFilters({
      category: categories.length === 1 ? categories[0] : null, // Single category for NDA
      categories: categories // Array for sNDA multiple selection
    });
    setTimeout(() => {
      this.navigateToStep('therapeutic');
    }, 300);
  }

  selectTherapeuticArea(area: string) {
    this.updateFilters({ therapeuticArea: area, therapeuticAreas: area.split(',') });
    setTimeout(() => {
      this.navigateToStep('results');
    }, 300);
  }
  getStatistics(filter_type: string, current_month: string, current_year: string, duration?: string): Observable<any> {

    return this.http.get(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.PADUFA.PDUFA_CALENDAR}?filter_type=${filter_type}&current_month=${current_month}&current_year=${current_year}&user_email=${localStorage.getItem('email') || ''}${duration ? `&duration=${duration}` : ''}`
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }
  getAllStatistics(): Observable<any> {

    return this.http.get(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.PADUFA.PDUFA_CALENDAR}?user_email=${localStorage.getItem('email') || ''}`
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }
  pdufaInformation(): Observable<any> {
    return this.http.get(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.PADUFA.PDUFA_INFORMATION}`,
      { params: { user_email: localStorage.getItem('email') || '' } }
    ).pipe(
      tap((res: any) => {
        this.pdufaInformationSubject.next(res);
      }),
      catchError((error) => {
        this.hideLoading();
        return this.errorHandlerService.handleError(error);
      })
    );
  }
  saveDrug(drug: any): Observable<any> {
    return this.http.post(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.PADUFA.PDUFA_INFORMATION}`,
      drug
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  deleteDrug(id: string): Observable<any> {
    return this.http.delete(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.PADUFA.PDUFA_INFORMATION}/?_id=${id}`
    ).pipe(catchError((error) => {
      this.errorHandlerService.handleError(error);
      return of(null);
    }));
  }

  updateDrug(drug: any): Observable<any> {
    return this.http.put(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.PADUFA.PDUFA_INFORMATION}/${drug._id}`,
      drug
    ).pipe(catchError((error) => {
      this.errorHandlerService.handleError(error);
      return of(null);
    }));
  }

  updateDrugPublish(drug: any): Observable<any> {
    return this.http.put(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.PADUFA.PDUFA_INFORMATION}/`,
      drug
    ).pipe(catchError((error) => {
      this.errorHandlerService.handleError(error);
      return of(null);
    }));
  }

  getDrugData(data: any): Observable<any> {

    return this.http.post(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.PADUFA.PDUFA_INFORMATION_DATA}`,
      data
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }
  psCalculate(): Observable<any> {

    return this.http.get(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.PADUFA.PDUFA_REFRESH_DATA}`
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  psCalculatePost(data: any): Observable<any> {

    return this.http.post(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.PADUFA.PDUFA_ANALOG_CALCULATE}`,
      data
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }
  allModuleDataGet(selectedMoleculeData: any): Observable<any> {
    return this.http.get(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.PADUFA.PDUFA_ANALOG_CALCULATE}`,
      {
        params: selectedMoleculeData   // ✅ pass as query params
      }
    ).pipe(
      catchError((error) => this.errorHandlerService.handleError(error))
    );
  }
  getRefreshData(): Observable<any> {
    return this.http.get(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.PADUFA.PDUFA_REFRESH_DATA}`,
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }
}
